import Button from '@/components/ui/button/button.component';
import { Pill } from '@/components/ui/pill/pill.component';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover/popover.component';
import { InclusionSource, ProductBuildingBlock } from '@/gql/graphql';
import { SortBy } from '@/hooks/use-building-blocks/use-building-blocks.hook.core';
import { cn } from '@/lib/utils';

import HandIcon from '@/assets/icons/hand.svg?react';
import { T } from '@/components/i18n/message.i18n.component';
import { pretailoringPageMessages } from '@/pages/products/pre-tailoring/pre-tailoring.messages';

type Props = {
  context: 'included' | 'excluded';
  className?: string;
  selected?: boolean;
  sortedBy?: SortBy;
  buildingBlock?: ProductBuildingBlock;
  onBlockClick?: (bb: ProductBuildingBlock) => void;
};

export const SingleBuildingBlock = ({
  context,
  className,
  selected,
  sortedBy,
  onBlockClick,
  buildingBlock: bb,
}: Props) => {
  const handleBlockClick = () => {
    if (!onBlockClick || !bb) return;
    onBlockClick(bb);
  };

  if (!bb) return null;

  return (
    <div
      data-testid={`building-block-${context}-${bb.id}`}
      key={bb.id}
      className={cn(
        'flex-1 grid grid-cols-[1fr_auto] grid-rows-[1fr_auto] border rounded border-other-stroke px-3 py-2',
        selected && 'border-primary',
        className,
      )}
    >
      <Button
        onClick={handleBlockClick}
        variant="ghost"
        aria-label={`Building block title for ${bb.name}`}
        className="h-auto whitespace-normal flex-inline mb-2 justify-start"
        data-testid={`building-block-${context}-${bb.id}-navigation-button`}
      >
        <p
          data-testid={`building-block-${context}-${bb.id}-name`}
          className="typo-primary-300 text-left hover:underline"
        >
          {bb.name}
        </p>
      </Button>
      {bb.inclusion.source === InclusionSource.Manual && (
        <Popover>
          <PopoverTrigger
            data-testid={`building-block-${context}-${bb.id}-exclusion-popover-trigger`}
            className={cn(
              'bg-other-stroke rounded flex justify-center items-center h-6 w-6 -mr-3 -mt-2',
              selected && 'bg-primary',
            )}
          >
            <div>
              <HandIcon className={cn('w-4 h-4 text-secondary-text', selected && 'text-white')} />
            </div>
          </PopoverTrigger>
          <PopoverContent aria-label="Building block is manualy excluded popover" align="center" side="left">
            <p className="typo-secondary" data-testid={`building-block-${context}-${bb.id}-exclusion-popover-content`}>
              {context === 'included' ? (
                <T {...pretailoringPageMessages.buildingBlockCardManualyIncluded} />
              ) : (
                <T {...pretailoringPageMessages.buildingBlockCardManualyExcluded} />
              )}
            </p>
          </PopoverContent>
        </Popover>
      )}
      <div className="col-span-2 flex flex-row">
        <div className="flex flex-row flex-wrap gap-2 ">
          {sortedBy === 'DO' &&
            bb.maturityStages?.map((mt) => (
              <Pill
                key={mt.id}
                variant="ms"
                data-testid={`building-block-${context}-${bb.id}-maturity-stage-${mt.id}`}
                text={mt.name as string}
              />
            ))}
        </div>
        {sortedBy === 'MTS' && (
          <Pill
            key={bb.designObligation?.id}
            variant="do"
            data-testid={`building-block-${context}-${bb.id}-design-obligation`}
            text={bb.designObligation?.name}
          />
        )}
      </div>
    </div>
  );
};
